/**
 * Upper tails of the discrete distributions, computed directly.
 *
 * `distributions.ts` gives P(X ≤ k). The complement P(X ≥ k) = 1 − P(X ≤ k − 1) is
 * exact on paper and useless in floating point once the tail is small: P(X ≥ 95) for
 * 100 fair coin flips is about 6 × 10⁻²³, and 1 minus a number that rounds to 1 is 0. So
 * the upper tails are evaluated from their own closed forms — the incomplete beta for
 * the binomial, the incomplete gamma for the Poisson, and a direct sum from the top of
 * the support for the hypergeometric. Pinned against `scipy.stats` `sf` in
 * `stats-distributions.test.ts`.
 */

import { hypergeometricPmf } from "@/lib/stats/distributions";
import { regularizedBeta, regularizedGammaP } from "@/lib/stats/special";

/** P(X ≥ k) for X ~ Binomial(n, p) = I_p(k, n − k + 1). */
export function binomialUpper(k: number, n: number, p: number): number {
  const start = Math.ceil(k);
  if (start <= 0) {
    return 1;
  }
  if (start > n) {
    return 0;
  }
  return regularizedBeta(p, start, n - start + 1);
}

/** P(X ≥ k) for X ~ Poisson(λ) = P(k, λ), the lower regularized gamma. */
export function poissonUpper(k: number, lambda: number): number {
  const start = Math.ceil(k);
  if (start <= 0) {
    return 1;
  }
  if (lambda === 0) {
    return 0;
  }
  return regularizedGammaP(start, lambda);
}

/** P(X ≥ k) for the hypergeometric, summed downward from the top of the support. */
export function hypergeometricUpper(
  k: number,
  population: number,
  successes: number,
  draws: number
): number {
  const minK = Math.max(0, draws - (population - successes));
  const maxK = Math.min(successes, draws);
  const start = Math.max(Math.ceil(k), minK);
  if (start > maxK) {
    return 0;
  }
  if (start <= minK) {
    return 1;
  }
  let total = 0;
  for (let i = maxK; i >= start; i--) {
    total += hypergeometricPmf(i, population, successes, draws);
  }
  return Math.min(1, total);
}
